import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import advancedBodyweightTrainers from '../data/trainers/AdvancedBodyweightTrainers';
import './AdvancedTrainerPage.css'; // Use the green-themed CSS

const AdvancedTrainerPage = () => {
    const navigate = useNavigate();
    const { trainerId } = useParams();

    // State for trainer and program selection
    const [trainer, setTrainer] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [selectedIntensity, setSelectedIntensity] = useState(100);
    const [showIntensityModal, setShowIntensityModal] = useState(false);
    const [currentRound, setCurrentRound] = useState(1);

    // NEW: State for tracking completed programs
    const [completedPrograms, setCompletedPrograms] = useState({});

    // Load advanced trainer data based on ID
    useEffect(() => {
        console.log('AdvancedTrainerPage mounted with trainerId:', trainerId);

        const trainerData = advancedBodyweightTrainers[trainerId];
        console.log('Found trainer data for', trainerId, ':', trainerData);

        if (trainerData) {
            setTrainer(trainerData);
        } else {
            console.log('No trainer found, redirecting...');
            navigate('/advanced-bodyweight-trainers');
        }

        // Initialize or get current round from localStorage
        const savedRound = localStorage.getItem('currentGameRound');
        if (savedRound) {
            setCurrentRound(parseInt(savedRound, 10));
        }
    }, [trainerId, navigate]);

    // NEW: Function to analyze saved workouts and track completed trainer programs
    const analyzeCompletedPrograms = () => {
        try {
            const savedWorkouts = JSON.parse(localStorage.getItem('savedWorkouts') || '[]');
            const completed = {};

            savedWorkouts.forEach(workout => {
                // Check if this is a trainer program workout (including advanced)
                if ((workout.isTrainerProgram || workout.isAdvancedTrainerProgram) && workout.trainerName && workout.programName) {
                    // Create a unique key for this trainer + program combination
                    const key = `${workout.trainerName}-${workout.programName}`;

                    // Initialize or increment the count
                    if (completed[key]) {
                        completed[key].count += 1;
                        // Keep track of the most recent completion date
                        if (new Date(workout.date) > new Date(completed[key].lastCompleted)) {
                            completed[key].lastCompleted = workout.date;
                        }
                    } else {
                        completed[key] = {
                            trainerName: workout.trainerName,
                            programName: workout.programName,
                            count: 1,
                            lastCompleted: workout.date,
                            intensityLevels: [workout.intensityLevel || 100] // Track intensity levels used
                        };
                    }

                    // Add intensity level if not already tracked
                    if (!completed[key].intensityLevels.includes(workout.intensityLevel)) {
                        completed[key].intensityLevels.push(workout.intensityLevel || 100);
                    }
                }
            });

            setCompletedPrograms(completed);
            console.log('Advanced completed programs analysis:', completed);
        } catch (error) {
            console.error('Error analyzing advanced completed programs:', error);
            setCompletedPrograms({});
        }
    };

    // NEW: Function to get completion status for a specific program
    const getProgramCompletionStatus = (programName) => {
        if (!trainer) return { completed: false, count: 0 };

        const key = `${trainer.name}-${programName}`;
        const completion = completedPrograms[key];

        return {
            completed: !!completion,
            count: completion ? completion.count : 0,
            lastCompleted: completion ? completion.lastCompleted : null,
            intensityLevels: completion ? completion.intensityLevels : []
        };
    };

    // NEW: Analyze completed programs when trainer loads or when component mounts
    useEffect(() => {
        analyzeCompletedPrograms();
    }, [trainer]);

    // Intensity level options (updated to include Game Mode)
    const intensityLevels = [
        {
            level: 100,
            label: 'Elite',
            description: 'Full intensity - as designed by the master trainer',
            color: '#00FF66',
            multiplier: 1.0
        },
        {
            level: 75,
            label: 'Advanced',
            description: '75% intensity - challenging but manageable',
            color: '#FFD700',
            multiplier: 0.75
        },
        {
            level: 50,
            label: 'Intermediate',
            description: '50% intensity - building towards advanced',
            color: '#FF8C00',
            multiplier: 0.5
        },
        {
            level: 25,
            label: 'Beginner',
            description: '25% intensity - learning the movements',
            color: '#FF0000',
            multiplier: 0.25
        },
        {
            level: 'game',
            label: 'GAME MODE',
            description: `Progressive card game: Round ${currentRound} = ${currentRound} ${currentRound === 1 ? 'rep' : 'reps'} per exercise`,
            color: '#8A2BE2',
            multiplier: 'game',
            isGameMode: true
        }
    ];

    // Handle advancing to next round (for game mode)
    const handleNextRound = () => {
        const nextRound = currentRound + 1;
        setCurrentRound(nextRound);
        localStorage.setItem('currentGameRound', nextRound.toString());
        console.log(`🎮 Advanced to Round ${nextRound}`);
    };

    // Handle resetting game mode
    const handleResetGame = () => {
        setCurrentRound(1);
        localStorage.setItem('currentGameRound', '1');
        console.log('🎮 Game reset to Round 1');
    };

    // Handle program selection
    const handleProgramSelect = (program) => {
        soundManager.playSound('advancedRep2', 0.6);
        setSelectedProgram(program);
    };

    // Handle start workout - show intensity modal
    const handleStartWorkout = () => {
        if (!selectedProgram) {
            alert('Please select a workout program first!');
            return;
        }

        soundManager.playSound('advancedStart2', 0.7);
        setShowIntensityModal(true);
    };

    // Handle intensity selection and start workout - GOES TO ADVANCED FLOW
    const handleIntensitySelect = (intensity) => {
        soundManager.playSound('gameIntroMode2', 0.8);
        setSelectedIntensity(intensity.level);
        setShowIntensityModal(false);

        let adjustedReps, adjustedSets;
        let isGameMode = false;
        let gameRound = null;

        if (intensity.multiplier === 'game') {
            // GAME MODE: Progressive reps based on current round
            adjustedReps = currentRound; // Round 1 = 1 rep, Round 2 = 2 reps, etc.
            adjustedSets = 1; // Always 1 set in game mode
            isGameMode = true;
            gameRound = currentRound;
            console.log(`🎮 ADVANCED GAME MODE: Round ${currentRound} = ${adjustedReps} reps`);
        } else {
            // Calculate adjusted reps and sets based on intensity
            adjustedReps = Math.max(1, Math.round(selectedProgram.reps * intensity.multiplier));
            adjustedSets = Math.max(1, Math.round(selectedProgram.sets * intensity.multiplier));
        }

        // Use the ADVANCED workout pattern (not regular bodyweight)
        const workoutCode = selectedProgram.pattern;

        // Navigate to ADVANCED workout flow (not regular bodyweight)
        navigate('/workout-summary', {
            state: {
                workoutCode: workoutCode,
                workoutType: 'advanced-bodyweight', // THIS IS KEY - routes to advanced flow
                repCount: adjustedReps,
                setCount: adjustedSets,
                difficultyLevel: 4, // Expert level for advanced trainer programs
                selectedMuscleGroups: workoutCode,
                // Trainer program info - UPDATED to include both flags for compatibility
                isTrainerProgram: true, // Keep this for general trainer detection
                isAdvancedTrainerProgram: true, // Flag for advanced trainer
                trainerName: trainer.name,
                programName: selectedProgram.name,
                programDescription: selectedProgram.description,
                programFocus: selectedProgram.focus,
                intensityLevel: isGameMode ? 100 : intensity.level,
                intensityLabel: isGameMode ? `Game Mode - Round ${currentRound}` : intensity.label,
                isGameMode: isGameMode,
                currentRound: gameRound
            }
        });
    };

    // Handle back navigation
    const handleBack = () => {
        soundManager.playButtonSound();
        navigate('/advanced-bodyweight-trainers');
    };

    if (!trainer) {
        return (
            <div className="trainer-loading">
                <div className="loading-spinner"></div>
                <p>Loading advanced trainer...</p>
            </div>
        );
    }

    return (
        <div className="advanced-theme trainer-page-container">
            {/* Green pulsating background for advanced trainers */}
            <div
                className="trainer-background"
                style={{
                    backgroundImage: `url(${trainer.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="trainer-overlay" style={{
                    background: 'linear-gradient(135deg, rgba(0, 255, 102, 0.4), rgba(0, 204, 82, 0.2))',
                    animation: 'greenPulse 8s ease-in-out infinite'
                }}></div>
            </div>

            {/* Back button */}
            <button className="back-button" onClick={handleBack}>
                ← Back to Advanced Trainers
            </button>

            {/* Trainer header */}
            <header className="trainer-header">
                <div className="trainer-photo">
                    <img src={trainer.image} alt={trainer.name} />
                    <div className="experience-badge" style={{ background: 'linear-gradient(45deg, #00FF66, #00CC52)' }}>
                        {trainer.experience}
                    </div>
                </div>
                <div className="trainer-info">
                    <h1 className="trainer-name" style={{ color: '#00FF66' }}>{trainer.name}</h1>
                    <h2 className="trainer-specialization" style={{ color: '#B3FFB3' }}>{trainer.specialization}</h2>
                    <p className="trainer-intro">{trainer.intro}</p>
                </div>
            </header>

            {/* Programs section */}
            <section className="programs-section">
                <h3 style={{ color: '#00FF66' }}>Elite Training Programs</h3>
                <p>Select an advanced 6-week program designed by {trainer.name}</p>

                <div className="programs-grid">
                    {trainer.programs.map((program) => {
                        // NEW: Get completion status for this program
                        const completionStatus = getProgramCompletionStatus(program.name);

                        return (
                            <div
                                key={program.id}
                                className={`program-card ${selectedProgram?.id === program.id ? 'selected' : ''} ${completionStatus.completed ? 'completed' : ''}`}
                                onClick={() => handleProgramSelect(program)}
                                style={{
                                    borderColor: selectedProgram?.id === program.id ? '#00FF66' : 'rgba(0, 255, 102, 0.3)',
                                    background: selectedProgram?.id === program.id
                                        ? 'linear-gradient(135deg, rgba(0, 255, 102, 0.3), rgba(0, 204, 82, 0.1))'
                                        : 'linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 255, 102, 0.1))'
                                }}
                                data-completion-count={completionStatus.count}
                            >
                                {/* NEW: Completion indicator */}
                                {completionStatus.completed && (
                                    <div className="completion-indicator">
                                        <span className="checkmark">✓</span>
                                        {completionStatus.count > 1 && (
                                            <span className="completion-count">{completionStatus.count}</span>
                                        )}
                                    </div>
                                )}

                                <div className="program-header">
                                    <h4 className="program-name" style={{ color: '#00FF66' }}>{program.name}</h4>
                                    <div className="program-stats">
                                        <span className="stat" style={{ background: 'rgba(0, 255, 102, 0.3)' }}>{program.sets} sets</span>
                                        <span className="stat" style={{ background: 'rgba(0, 255, 102, 0.3)' }}>{program.reps} reps</span>
                                    </div>
                                </div>
                                <p className="program-description">{program.description}</p>

                                {/* NEW: Show completion details if completed */}
                                {completionStatus.completed && (
                                    <div className="completion-details">
                                        <small>
                                            Completed {completionStatus.count} time{completionStatus.count > 1 ? 's' : ''}
                                            {completionStatus.lastCompleted && (
                                                <span> • Last: {new Date(completionStatus.lastCompleted).toLocaleDateString()}</span>
                                            )}
                                        </small>
                                    </div>
                                )}

                                <div className="program-features">
                                    <span className="feature" style={{ background: 'rgba(0, 255, 102, 0.2)', color: '#00FF66' }}>6 Week Program</span>
                                    <span className="feature" style={{ background: 'rgba(0, 255, 102, 0.2)', color: '#00FF66' }}>Advanced</span>
                                    {completionStatus.completed && (
                                        <span className="feature completed-feature" style={{ background: '#4CAF50', color: 'white' }}>Completed</span>
                                    )}
                                    <span className="feature pattern-debug" style={{ background: 'rgba(255, 215, 0, 0.2)' }}>Pattern: {program.pattern}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Selected program details */}
            {selectedProgram && (
                <section className="selected-program-section">
                    <div className="program-details-card" style={{
                        background: 'linear-gradient(135deg, rgba(0, 255, 102, 0.15), rgba(0, 204, 82, 0.05))',
                        borderColor: '#00FF66'
                    }}>
                        <h4 style={{ color: '#00FF66' }}>{selectedProgram.name} - Program Details</h4>
                        <div className="program-content">
                            <p><strong>Description:</strong> {selectedProgram.description}</p>
                            <p><strong>Default Settings:</strong> {selectedProgram.sets} sets × {selectedProgram.reps} reps</p>
                            <p><strong>Focus:</strong> {selectedProgram.focus}</p>
                            <p><strong>Advanced Pattern:</strong> <code style={{ color: '#00FF66' }}>{selectedProgram.pattern}</code></p>

                            {/* NEW: Show completion history for selected program */}
                            {(() => {
                                const status = getProgramCompletionStatus(selectedProgram.name);
                                if (status.completed) {
                                    return (
                                        <div className="selected-program-history">
                                            <p><strong>Your Progress:</strong> Completed {status.count} time{status.count > 1 ? 's' : ''}</p>
                                            {status.intensityLevels.length > 0 && (
                                                <p><strong>Intensity Levels Completed:</strong> {status.intensityLevels.sort((a, b) => b - a).join('%, ')}%</p>
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>

                        <button
                            className="start-program-button"
                            onClick={handleStartWorkout}
                            style={{ background: 'linear-gradient(45deg, #00FF66, #00CC52)' }}
                        >
                            <span className="button-icon">🚀</span>
                            <span className="button-text">
                                {getProgramCompletionStatus(selectedProgram.name).completed ? 'Restart Elite Program' : 'Start Elite Program'}
                            </span>
                        </button>
                    </div>
                </section>
            )}

            {/* Intensity selection modal */}
            {showIntensityModal && (
                <div className="intensity-modal-overlay" onClick={() => setShowIntensityModal(false)}>
                    <div className="intensity-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ color: '#00FF66' }}>Choose Your Elite Level</h3>
                        <p>Select the intensity that matches your advanced training level</p>

                        {/* Game Mode Round Display */}
                        <div className="game-mode-status-modal">
                            <div className="current-round-display">
                                <h4>🎮 Current Game Round: {currentRound}</h4>
                                <div className="round-controls">
                                    <button className="round-button-small" onClick={handleNextRound}>
                                        Advance to Round {currentRound + 1}
                                    </button>
                                    <button className="round-button-small reset" onClick={handleResetGame}>
                                        Reset to Round 1
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="intensity-options">
                            {intensityLevels.map((intensity) => {
                                // NEW: Show if user has completed this program at this intensity
                                const programStatus = getProgramCompletionStatus(selectedProgram.name);
                                const hasCompletedAtThisIntensity = intensity.isGameMode ?
                                    programStatus.intensityLevels.includes('game') :
                                    programStatus.intensityLevels.includes(intensity.level);

                                return (
                                    <div
                                        key={intensity.level}
                                        className={`intensity-option ${hasCompletedAtThisIntensity ? 'previously-completed' : ''} ${intensity.isGameMode ? 'game-mode-option' : ''}`}
                                        style={{
                                            borderColor: intensity.color,
                                            background: intensity.isGameMode ?
                                                'linear-gradient(135deg, rgba(138, 43, 226, 0.95), rgba(255, 20, 147, 0.9), rgba(255, 215, 0, 0.85))' :
                                                `linear-gradient(135deg, ${intensity.color}20, ${intensity.color}10)`
                                        }}
                                        onClick={() => handleIntensitySelect(intensity)}
                                    >
                                        {hasCompletedAtThisIntensity && (
                                            <div className="intensity-completed-indicator">✓</div>
                                        )}
                                        <div className="intensity-level" style={{ color: intensity.color }}>
                                            {intensity.isGameMode ? '✨' : `${intensity.level}%`}
                                        </div>
                                        <div className="intensity-label">{intensity.label}</div>
                                        <div className="intensity-description">{intensity.description}</div>
                                        <div className="intensity-preview" style={{ color: intensity.color }}>
                                            {intensity.isGameMode ?
                                                `${currentRound} reps × 1 set` :
                                                `${Math.round(selectedProgram.reps * intensity.multiplier)} reps × ${Math.round(selectedProgram.sets * intensity.multiplier)} sets`
                                            }
                                        </div>
                                        {intensity.isGameMode && (
                                            <div className="game-mode-preview-text">
                                                <small>Next round will be {currentRound + 1} reps per exercise</small>
                                            </div>
                                        )}
                                        {hasCompletedAtThisIntensity && (
                                            <div className="intensity-completed-text">Previously completed</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            className="close-modal-button"
                            onClick={() => setShowIntensityModal(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvancedTrainerPage;